import React from 'react';
import Button from '../../components/Button';
import { ReactComponent as Sparkle } from '../../assets/sparkle.svg';

const FEATURES = [
  {
    title: 'One-Click Deploy',
    description:
      'Guided setup wizard. No servers, no Docker, no command line.',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='w-6 h-6'
      >
        <path d='M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z' />
        <path d='m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z' />
        <path d='M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0' />
        <path d='M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5' />
      </svg>
    ),
  },
  {
    title: 'Multi-Model AI',
    description:
      'Claude, GPT, Gemini, DeepSeek, or Grok. Bring your own key.',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='w-6 h-6'
      >
        <path d='M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.58-3.25 3.93a1 1 0 0 0-.75.97V13' />
        <path d='M12 2a4 4 0 0 0-4 4c0 1.95 1.4 3.58 3.25 3.93a1 1 0 0 1 .75.97V13' />
        <circle cx='12' cy='17' r='4' />
        <path d='M10 17h4' />
        <path d='M12 15v4' />
      </svg>
    ),
  },
  {
    title: 'Chat-First Interface',
    description:
      'Talk to your agent on Telegram. Discord and Slack coming soon.',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='w-6 h-6'
      >
        <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
        <path d='M8 10h.01' />
        <path d='M12 10h.01' />
        <path d='M16 10h.01' />
      </svg>
    ),
  },
  {
    title: 'Wayfinder SDK Built-In',
    description:
      'DeFi strategies, swaps, lending, bridging, and perps pre-installed.',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='w-6 h-6'
      >
        <polyline points='16 18 22 12 16 6' />
        <polyline points='8 6 2 12 8 18' />
        <line x1='14' y1='4' x2='10' y2='20' />
      </svg>
    ),
  },
  {
    title: 'Self-Custody Wallets',
    description:
      'Your agent gets its own wallet. You hold the keys, always.',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='w-6 h-6'
      >
        <rect x='2' y='6' width='20' height='14' rx='2' />
        <path d='M2 10h20' />
        <path d='M16 14h2' />
      </svg>
    ),
  },
  {
    title: '24/7 Autonomous Operation',
    description:
      'Dedicated cloud instances. Schedule jobs and automate strategies.',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='w-6 h-6'
      >
        <circle cx='12' cy='12' r='10' />
        <polyline points='12 6 12 12 16 14' />
      </svg>
    ),
  },
];

function FeatureItem({ title, description, icon }) {
  return (
    <div className='flex gap-4 p-5 rounded-2xl bg-gradient-to-r from-white/3 via-white/5 to-white/3'>
      <div className='flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-mint/16 text-mint'>
        {icon}
      </div>
      <div>
        <h3 className='text-white font-semibold mb-1'>{title}</h3>
        <p className='text-white/60 text-13 leading-relaxed'>{description}</p>
      </div>
    </div>
  );
}

export default function CloudAgentsSection() {
  return (
    <section
      id='cloud-agents'
      className='flex items-center justify-center my-26'
    >
      <div className='w-full max-w-5xl sm:px-6 px-6'>
        <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12'>
          <div className='space-y-4'>
            <Button
              color='outlineGray'
              size='rounded'
              className='pointer-events-none'
            >
              <Sparkle />
              Cloud Agents
            </Button>
            <h2 className='text-4.5xl font-semibold leading-[141%]'>
              Deploy your own AI agent
            </h2>
            <p className='text-white/60 text-lg md:text-base max-w-lg'>
              Powered by OpenClaw and the Wayfinder SDK. A dedicated, always-on
              AI agent that executes DeFi strategies on your behalf.
            </p>
          </div>
          <div className='flex gap-4 flex-shrink-0'>
            <Button
              color='mint'
              size='md'
              href='https://strategies.wayfinder.ai'
            >
              Deploy an Agent
            </Button>
            <Button
              color='outline'
              size='md'
              href='https://github.com/WayfinderFoundation/wayfinder-paths-sdk'
              target='_blank'
              rel='noreferrer'
            >
              GitHub
            </Button>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {FEATURES.map((feature) => (
            <FeatureItem
              key={feature.title}
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
